// src/hooks/sync/useInviteLink.js
// Handles an invite link (/join/<prayer code>). App.jsx stores the code in localStorage before
// anything renders; once the new user is signed in and set up, this links them to the inviter
// (a prayerPartners doc, same shape the Prayer partners dialog writes) and, if they have no mentor
// yet, sets the inviter as their mentor. Called from useSharedPrayerSync (mounted once in Layout).
// On a network failure the code stays stored and is retried on the next app launch.
import { useEffect, useRef, useState } from 'react';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { toast } from 'sonner';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useSyncStore } from '@/hooks/sync/useSyncStore';
import { STORE_USER_PROFILE } from '@/lib/storeKeys';

const INVITE_KEY = 'sheepheard:pendingInvite';
const clearInvite = () => { try { localStorage.removeItem(INVITE_KEY); } catch { /* storage unavailable */ } };

export function useInviteLink({ partners, loaded, reload }) {
  const { user } = useAuth();
  const uid = user?.uid && user.uid !== 'offline' ? user.uid : null;
  const [profile, setProfile, profileReady] = useSyncStore(STORE_USER_PROFILE, {});
  const doneRef = useRef(false);
  const [tick, setTick] = useState(0);

  // An invite link opened while the app is already running (see App.jsx appUrlOpen handler).
  useEffect(() => {
    const onInvite = () => { doneRef.current = false; setTick((t) => t + 1); };
    window.addEventListener('sheepheard:invite', onInvite);
    return () => window.removeEventListener('sheepheard:invite', onInvite);
  }, []);

  useEffect(() => {
    if (doneRef.current || !uid || !db || !profileReady || !loaded) return;
    let code = null;
    try { code = localStorage.getItem(INVITE_KEY); } catch { /* storage unavailable */ }
    doneRef.current = true;
    if (!code) return;

    (async () => {
      try {
        const snap = await getDoc(doc(db, 'prayerCodes', code));
        if (!snap.exists()) {
          clearInvite();
          toast.error('That invite link is no longer valid. Ask for a new one.');
          return;
        }
        const toUid  = snap.data().uid;
        const toName = String(snap.data().name || 'Friend').slice(0, 60);
        if (!toUid || toUid === uid) { clearInvite(); return; }

        if (!partners.some((p) => p.uid === toUid)) {
          const myName = String(profile?.displayName || user?.displayName || '').trim().slice(0, 60) || 'A friend';
          await setDoc(doc(db, 'prayerPartners', `${toUid}_${uid}`), {
            toUid, toName, fromUid: uid, fromName: myName, code, createdAt: serverTimestamp(),
          });
        }

        if (!profile?.mentorUid) {
          setProfile((prev) => ({ ...(prev || {}), mentorUid: toUid, mentorName: toName }));
          toast.success(`Linked with ${toName}. They are now your mentor.`);
        } else {
          toast.success(`Linked with ${toName}. You can change your mentor in Prayer partners.`);
        }
        clearInvite();
        reload();
      } catch (err) {
        console.warn('[useInviteLink] failed, will retry next launch:', err?.code || err);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, profileReady, loaded, partners, profile, tick]);
}

export default useInviteLink;